export default class StringUtils {
	// Returns a 0–1 similarity. We keep the best of two complementary measures:
	// the Sørensen–Dice bigram coefficient (good at matching overall word shape /
	// shared substrings) and a normalized Damerau–Levenshtein distance (good at
	// the actual typing mistakes — a missing/extra/wrong letter, or two adjacent
	// letters swapped). Dice alone is harsh on short words and especially on
	// transpositions ("allemange" for "allemagne"), which is why typos felt
	// unforgiving. Both inputs are sanitized so accents/spaces/punctuation never
	// matter.
	static similarity(a, b) {
		a = this.prep(this.sanitize(a));
		b = this.prep(this.sanitize(b));
		if (a === b) return 1;
		if (!a.length || !b.length) return 0;
		return Math.max(this.diceCoefficient(a, b), this.editSimilarity(a, b));
	}

	static diceCoefficient(a, b) {
		const bg1 = this.bigrams(a);
		const bg2 = this.bigrams(b);
		if (!bg1.length || !bg2.length) return a === b ? 1 : 0;
		const c1 = this.count(bg1);
		const c2 = this.count(bg2);
		const combined = this.uniq([...bg1, ...bg2])
			.reduce((t, k) => t + (Math.min(c1[k] || 0, c2[k] || 0)), 0);
		return 2 * combined / (bg1.length + bg2.length);
	}

	// 1 − (edit distance / longest length). Damerau–Levenshtein counts a swap of
	// two adjacent letters as a single edit, matching how people actually mistype.
	static editSimilarity(a, b) {
		return 1 - this.damerauLevenshtein(a, b) / Math.max(a.length, b.length);
	}

	// Optimal string alignment distance (Damerau–Levenshtein restricted to
	// adjacent transpositions). O(m·n) — fine for country-name-sized strings.
	static damerauLevenshtein(a, b) {
		const m = a.length, n = b.length;
		if (!m) return n;
		if (!n) return m;
		const d = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
		for (let i = 0; i <= m; i++) d[i][0] = i;
		for (let j = 0; j <= n; j++) d[0][j] = j;
		for (let i = 1; i <= m; i++) {
			for (let j = 1; j <= n; j++) {
				const cost = a[i - 1] === b[j - 1] ? 0 : 1;
				d[i][j] = Math.min(
					d[i - 1][j] + 1,        // deletion
					d[i][j - 1] + 1,        // insertion
					d[i - 1][j - 1] + cost  // substitution
				);
				if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
					d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1); // transposition
				}
			}
		}
		return d[m][n];
	}

	// Highest similarity of `input` to any of a candidate's accepted names.
	// `texts` may be a single string or an array (a country can have several
	// accepted names — e.g. "Bélarus"/"Biélorussie", "Myanmar"/"Birmanie").
	static maxSimilarity(input, texts) {
		const arr = Array.isArray(texts) ? texts : [texts];
		let best = 0;
		for (const t of arr) {
			const s = this.similarity(input, t);
			if (s > best) best = s;
		}
		return best;
	}

	// Closest candidate to `input` among `candidates`. `getTexts` extracts the
	// accepted name(s) to compare against — either a string or an array of
	// accepted names (aliases). `ambiguous` flags a near-tie with the runner-up
	// (within `margin`), so callers can refuse to guess between two plausible
	// answers — e.g. Iran vs Iraq — instead of silently accepting the wrong one.
	static bestMatch(input, candidates, getTexts = c => [c.sanitize, ...(c.sanitizeAliases || [])], margin = 0.08) {
		let item = null, best = -1, second = -1;
		candidates.forEach(c => {
			const s = this.maxSimilarity(input, getTexts(c));
			if (s > best) { second = best; best = s; item = c; }
			else if (s > second) { second = s; }
		});
		return { item, score: best < 0 ? 0 : best, ambiguous: best - second < margin };
	}

	// Decides whether a typed `input` should be accepted as `rightAnswer` in the
	// single-answer typed games (Flag/Map Nerd Hard, Daily Challenge hard). It
	// still forgives genuine typos (similarity ≥ `threshold`) but REFUSES an input
	// that is a closer — or near-tied — match to a *different* country. That stops
	// the exact name of a look-alike from slipping through the fuzzy threshold:
	// Niger↔Nigéria, Irlande↔Islande, Gambie↔Zambie, North↔South Korea all sit at
	// ≥0.78 similarity, so typing the wrong one used to validate. Mirrors the
	// `bestMatch` ambiguity guard already used by Country Nerd. `candidates` is the
	// pool of all possible countries; `getTexts` extracts the accepted name(s) —
	// a string or an array of aliases (e.g. "Bélarus"/"Biélorussie").
	static matchesAnswer(input, rightAnswer, candidates, getTexts = c => [c.sanitize, ...(c.sanitizeAliases || [])], threshold = 0.78, margin = 0.08) {
		const right = this.maxSimilarity(input, getTexts(rightAnswer));
		if (right < threshold) return false;

		let bestOther = -1;
		candidates.forEach(c => {
			if (c.code === rightAnswer.code) return;
			const s = this.maxSimilarity(input, getTexts(c));
			if (s > bestOther) bestOther = s;
		});

		return right - bestOther >= margin;
	}

	static prep(str) {
		return str.toLowerCase().replace(/[^\w\s]/g, ' ').replace(/\s+/g, ' ');
	}

	static bigrams(str) {
		return [...str].slice(0, -1).map((c, i) => c + str[i + 1]);
	}

	static count(xs) {
		return xs.reduce(function (a, x) {
			a[x] = (a[x] || 0) + 1;
			return a;
		}, {});
	}

	static uniq(xs) {
		return [...new Set(xs)];
	}

	static sanitize(value) {
		return value.toLowerCase().normalize("NFD")
			.replace(/[\u0300-\u036f]/g, "")
			.replace(/-/g, "")
			.replace(/'/g, "")
			.replace(/ /g, "")
			.replace(/’/g, "");
	}
}
